// Configuración centralizada de la aplicación InboMarket
import { formatCurrency } from '@/shared/utils/currency';

export const APP_CONFIG = {
  name: import.meta.env.VITE_APP_NAME || 'InboMarket',
  version: import.meta.env.VITE_APP_VERSION || '1.0.0',
  description: 'Plataforma inmobiliaria modular para LATAM',
  environment:
    import.meta.env.VITE_NODE_ENV || import.meta.env.MODE || 'development',
  api: {
    baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:3009/api/v1',
    version: 'v1',
    timeout: 30000,
  },
  auth: {
    tokenKey: 'inbomarket_token',
    refreshTokenKey: 'inbomarket_refresh_token',
    userKey: 'inbomarket_user',
  },
  features: {
    enableOfflineMode: true,
    enablePushNotifications: true,
    enableAnalytics: true,
  },
  roles: {
    // Roles Globales (CORE)
    SUPER_ADMIN: 'SUPER_ADMIN',

    // Roles de Negocio (BUSINESS)
    AGENCY_ADMIN: 'AGENCY_ADMIN',
    BROKER_MANAGER_ADMIN: 'BROKER_MANAGER_ADMIN',
    PROJECT_DEVELOPER_ADMIN: 'PROJECT_DEVELOPER_ADMIN',
    AGENCY_AGENT_USER: 'AGENCY_AGENT_USER',

    // Roles de Soporte (SUPPORT)
    ACCOUNTING_TEAM: 'ACCOUNTING_TEAM',
    OPERATIONAL_TEAM: 'OPERATIONAL_TEAM',
    CONDOMINIUM_ADMIN: 'CONDOMINIUM_ADMIN',

    // Roles Especializados (SPECIALIZED)
    LEGAL_MODULE: 'LEGAL_MODULE',

    // Roles de Cliente (CLIENT)
    BUYER_CLIENT: 'BUYER_CLIENT',
  },
  propertyTypes: {
    APARTMENT: 'apartamento',
    PENTHOUSE: 'penthouse',
    STUDIO: 'estudio',
    HOUSE: 'casa',
    DUPLEX: 'dúplex',
    VILLA: 'villa',
    FARM: 'finca',
    LAND: 'solar',
    COMMERCIAL: 'local',
    OFFICE: 'oficina',
    INDUSTRIAL: 'nave_industrial',
    BUILDING: 'edificio',
    HOTEL: 'hotel',
    RESORT: 'resort',
    MARINA: 'marina',
  },
  businessCategories: {
    SALE: 'venta',
    LONG_TERM_RENTAL: 'alquiler_largo_plazo',
    SHORT_TERM_RENTAL: 'alquiler_temporal',
    PRESALE: 'preventa',
    PLANS: 'proyecto_en_planos',
    AUCTION: 'remate',
    EXCHANGE: 'permuta',
    TIMESHARE: 'tiempo_compartido',
  },
  currency: {
    // Moneda por defecto
    default: 'USD',
    // Monedas disponibles en el sistema
    available: {
      USD: {
        code: 'USD',
        name: 'Dólar Americano',
        symbol: '$',
        symbolPosition: 'before' as const,
        locale: 'en-US',
        decimals: 2,
      },
      DOP: {
        code: 'DOP',
        name: 'Peso Dominicano',
        symbol: 'RD$',
        symbolPosition: 'before' as const,
        locale: 'es-DO',
        decimals: 2,
      },
      EUR: {
        code: 'EUR',
        name: 'Euro',
        symbol: '€',
        symbolPosition: 'after' as const,
        locale: 'es-ES',
        decimals: 2,
      },
      COP: {
        code: 'COP',
        name: 'Peso Colombiano',
        symbol: '$',
        symbolPosition: 'before' as const,
        locale: 'es-CO',
        decimals: 2,
      },
      MXN: {
        code: 'MXN',
        name: 'Peso Mexicano',
        symbol: '$',
        symbolPosition: 'before' as const,
        locale: 'es-MX',
        decimals: 2,
      },
      BTC: {
        code: 'BTC',
        name: 'Bitcoin',
        symbol: '₿',
        symbolPosition: 'before' as const,
        locale: 'en-US',
        decimals: 8,
      },
      ETH: {
        code: 'ETH',
        name: 'Ethereum',
        symbol: 'Ξ',
        symbolPosition: 'before' as const,
        locale: 'en-US',
        decimals: 8,
      },
    },
    // Monedas principales para uso en propiedades y transacciones
    primary: ['USD', 'DOP', 'EUR'] as const,
    // Monedas para uso en facturación
    billing: ['USD', 'DOP', 'EUR', 'COP', 'MXN'] as const,
    // Monedas cripto (si se necesitan por separado)
    crypto: ['BTC', 'ETH'] as const,
  },
} as const;

// Utilidades para manejo de entornos
export const isDevelopment = () => APP_CONFIG.environment === 'development';
export const isProduction = () => APP_CONFIG.environment === 'production';
export const isTesting = () => APP_CONFIG.environment === 'testing';

// Función para obtener la URL base según el entorno
export const getApiBaseUrl = () => {
  // Siempre usar VITE_API_URL si está definida
  return APP_CONFIG.api.baseUrl;
};

export type AppConfig = typeof APP_CONFIG;

// Tipos de Currency
export type CurrencyCode = keyof typeof APP_CONFIG.currency.available;
export type Currency = CurrencyCode;

// Información de una moneda
export interface CurrencyInfo {
  code: CurrencyCode;
  name: string;
  symbol: string;
  symbolPosition: 'before' | 'after';
  locale: string;
  decimals: number;
}

// Opciones para selects/dropdowns
export interface CurrencyOption {
  value: CurrencyCode;
  label: string;
}

/**
 * Obtiene la información completa de una moneda
 * @param code - Código de la moneda
 * @returns Información de la moneda o undefined si no existe
 */
export const getCurrencyInfo = (code: string): CurrencyInfo | undefined => {
  const currencyCode = code.toUpperCase() as CurrencyCode;
  return APP_CONFIG.currency.available[currencyCode];
};

/**
 * Obtiene el símbolo de una moneda
 * @param code - Código de la moneda
 * @returns Símbolo de la moneda o '$' por defecto
 */
export const getCurrencySymbol = (code: string): string => {
  const info = getCurrencyInfo(code);
  return info?.symbol || '$';
};

/**
 * Obtiene el nombre de una moneda
 * @param code - Código de la moneda
 * @returns Nombre de la moneda o 'USD' por defecto
 */
export const getCurrencyName = (code: string): string => {
  const info = getCurrencyInfo(code);
  return info?.name || 'Dólar Americano';
};

/**
 * Obtiene las opciones de moneda para selects/dropdowns
 * @param currencies - Lista de códigos de moneda a incluir (opcional, usa todas por defecto)
 * @returns Array de opciones para selects
 */
export const getCurrencyOptions = (
  currencies?: readonly CurrencyCode[]
): CurrencyOption[] => {
  const codes = currencies || (Object.keys(APP_CONFIG.currency.available) as CurrencyCode[]);
  return codes.map((code) => ({
    value: code,
    label: `${code} - ${getCurrencyName(code)}`,
  }));
};

/**
 * Obtiene las opciones de moneda principales (USD, DOP, EUR)
 */
export const getPrimaryCurrencyOptions = (): CurrencyOption[] => {
  return getCurrencyOptions(APP_CONFIG.currency.primary);
};

/**
 * Obtiene las opciones de moneda para facturación
 */
export const getBillingCurrencyOptions = (): CurrencyOption[] => {
  return getCurrencyOptions(APP_CONFIG.currency.billing);
};

/**
 * Lista de monedas comunes como fallback si Intl.supportedValuesOf no está disponible
 * Basada en ISO 4217
 */
const COMMON_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'JPY', 'CNY', 'AUD', 'CAD', 'CHF', 'HKD', 'SGD',
  'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'RUB', 'INR', 'BRL', 'ZAR',
  'MXN', 'ARS', 'CLP', 'COP', 'PEN', 'UYU', 'VES', 'DOP', 'GTQ', 'HNL',
  'NIO', 'CRC', 'PAB', 'BBD', 'BZD', 'JMD', 'TTD', 'BMD', 'BSD', 'KYD',
  'XCD', 'AWG', 'ANG', 'SRD', 'GYD', 'BWP', 'ZMW', 'ZWL', 'EGP', 'MAD',
  'TND', 'DZD', 'LYD', 'SDG', 'ETB', 'KES', 'UGX', 'TZS', 'RWF', 'BIF',
  'DJF', 'SOS', 'ERN', 'AED', 'SAR', 'OMR', 'YER', 'QAR', 'BHD', 'KWD',
  'IQD', 'IRR', 'ILS', 'JOD', 'LBP', 'SYP', 'TRY', 'GEL', 'AMD', 'AZN',
  'KZT', 'KGS', 'TJS', 'TMT', 'UZS', 'AFN', 'PKR', 'BDT', 'LKR', 'NPR',
  'MMK', 'THB', 'LAK', 'KHR', 'VND', 'IDR', 'MYR', 'PHP', 'SGD', 'BNB',
  'KRW', 'TWD', 'MOP', 'HKD', 'NZD', 'FJD', 'PGK', 'SBD', 'VUV', 'WST',
  'XPF', 'NZD', 'AUD', 'BTC', 'ETH',
] as const;

/**
 * Obtiene todas las monedas disponibles usando Intl API
 * Combina las monedas configuradas con las disponibles en el sistema
 * @param locale - Locale para los nombres de las monedas (default: 'es')
 * @returns Array de opciones para selects con todas las monedas disponibles
 */
export const getAllCurrencyOptionsFromIntl = (locale: string = 'es'): CurrencyOption[] => {
  try {
    // Verificar si Intl.supportedValuesOf está disponible (Chrome 99+, Firefox 97+, Safari 15.4+)
    let supportedCurrencies: string[];
    
    if (typeof Intl !== 'undefined' && 'supportedValuesOf' in Intl && typeof (Intl as any).supportedValuesOf === 'function') {
      try {
        supportedCurrencies = (Intl as any).supportedValuesOf('currency') as string[];
      } catch (e) {
        // Si falla, usar lista común
        supportedCurrencies = [...COMMON_CURRENCIES];
      }
    } else {
      // Fallback a lista común si la API no está disponible
      supportedCurrencies = [...COMMON_CURRENCIES];
    }
    
    // Crear un DisplayNames para obtener los nombres de las monedas
    let displayNames: Intl.DisplayNames;
    try {
      displayNames = new Intl.DisplayNames([locale], { type: 'currency' });
    } catch (e) {
      // Fallback a español si el locale falla
      displayNames = new Intl.DisplayNames(['es'], { type: 'currency' });
    }
    
    // Mapear a opciones, priorizando las configuradas en APP_CONFIG
    const currencyOptions: CurrencyOption[] = supportedCurrencies.map((code) => {
      const upperCode = code.toUpperCase() as CurrencyCode;
      
      // Si está en la configuración, usar el nombre personalizado
      const configInfo = APP_CONFIG.currency.available[upperCode];
      let name: string;
      
      if (configInfo?.name) {
        name = configInfo.name;
      } else {
        try {
          name = displayNames.of(code) || code;
        } catch (e) {
          name = code;
        }
      }
      
      return {
        value: upperCode,
        label: `${upperCode} - ${name}`,
      };
    });
    
    // Ordenar: primero las configuradas en billing, luego el resto alfabéticamente
    const billingCodes = APP_CONFIG.currency.billing.map(c => c.toUpperCase());
    currencyOptions.sort((a, b) => {
      const aInBilling = billingCodes.includes(a.value);
      const bInBilling = billingCodes.includes(b.value);
      
      if (aInBilling && !bInBilling) return -1;
      if (!aInBilling && bInBilling) return 1;
      if (aInBilling && bInBilling) {
        return billingCodes.indexOf(a.value) - billingCodes.indexOf(b.value);
      }
      return a.value.localeCompare(b.value);
    });
    
    return currencyOptions;
  } catch (error) {
    // Fallback a la configuración local si Intl no está disponible
    return getCurrencyOptions();
  }
};

/**
 * Obtiene las opciones de moneda para facturación usando Intl
 * Incluye todas las monedas disponibles pero prioriza las configuradas
 * @param locale - Locale para los nombres de las monedas (default: 'es')
 * @returns Array de opciones para selects
 */
export const getBillingCurrencyOptionsFromIntl = (locale: string = 'es'): CurrencyOption[] => {
  try {
    // Obtener todas las monedas disponibles
    const allCurrencies = getAllCurrencyOptionsFromIntl(locale);
    
    // Filtrar solo las que están en la configuración de billing, pero incluir todas como opción
    // Si quieres solo las configuradas, descomenta la siguiente línea:
    // return allCurrencies.filter(opt => APP_CONFIG.currency.billing.includes(opt.value));
    
    // Por defecto, devolvemos todas pero priorizadas
    return allCurrencies;
  } catch (error) {
    return getBillingCurrencyOptions();
  }
};

/**
 * Formatea un número como moneda usando la configuración de la moneda
 * @param amount - Cantidad a formatear
 * @param code - Código de la moneda
 * @returns String formateado como moneda
 */
export const formatCurrencyByCode = (
  amount: number | string,
  code: string = APP_CONFIG.currency.default
): string => {
  const info = getCurrencyInfo(code);
  const symbol = info?.symbol || '$';
  
  // Usar la función formatCurrency del sistema con el símbolo y configuración de la moneda
  // Formato con 1 decimal: 5.000,0
  return formatCurrency(amount, {
    symbol,
    symbolPosition: 'before',
    thousandsSeparator: '.',
    decimalSeparator: ',',
    decimals: 1,
    showDecimals: true,
  });
};
